import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { addPrivateClubMember, getUserAggregatedInfo, getUsersPaginated } from '../Api';
import AccountMainInfoDto from '../models/dtos/AccountMainInfoDto';
import PrivateClubAdminDto from '../models/dtos/PrivateClubAdminDto';

type AddPrivateClubMemberDialogProps = {
    open: boolean;
    clubId: string;
    clubName: string;
    onClose: () => void;
    onAdded: (club: PrivateClubAdminDto) => void | Promise<void>;
};

function userName(user: AccountMainInfoDto) {
    return user.name?.trim() || user.email?.trim() || `User ${user.id}`;
}

function userLabel(user: AccountMainInfoDto) {
    return `${userName(user)} - ID ${user.id}`;
}

function mergeUsers(users: AccountMainInfoDto[], selectedUser: AccountMainInfoDto | null) {
    const merged = selectedUser ? [selectedUser, ...users] : users;
    return Array.from(new Map(merged.map(user => [user.id, user])).values());
}

export default function AddPrivateClubMemberDialog({
    open,
    clubId,
    clubName,
    onClose,
    onAdded,
}: AddPrivateClubMemberDialogProps) {
    const [selectedUser, setSelectedUser] = React.useState<AccountMainInfoDto | null>(null);
    const [userOptions, setUserOptions] = React.useState<AccountMainInfoDto[]>([]);
    const [userSearch, setUserSearch] = React.useState('');
    const [usersLoading, setUsersLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!open) return;
        setSelectedUser(null);
        setUserOptions([]);
        setUserSearch('');
        setSubmitting(false);
        setError(null);
    }, [open]);

    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const timeoutId = window.setTimeout(async () => {
            setUsersLoading(true);
            const searchTerm = userSearch.trim();
            const exactUserId = /^\d+$/.test(searchTerm) ? Number(searchTerm) : null;
            const [response, exactUserResponse] = await Promise.all([
                getUsersPaginated(0, 20, searchTerm || undefined),
                exactUserId != null && exactUserId > 0
                    ? getUserAggregatedInfo(exactUserId)
                    : Promise.resolve(null),
            ]);
            if (!cancelled) {
                const users = exactUserResponse?.general
                    ? [exactUserResponse.general, ...(response?.items ?? [])]
                    : response?.items ?? [];
                setUserOptions(mergeUsers(users, selectedUser));
                setUsersLoading(false);
            }
        }, 300);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [open, selectedUser, userSearch]);

    const addMember = async () => {
        if (!selectedUser) {
            setError('User is required.');
            return;
        }
        setError(null);
        setSubmitting(true);
        const result = await addPrivateClubMember(clubId, selectedUser.id);
        setSubmitting(false);
        if (!result.ok || !result.data) {
            setError(result.error ?? 'User was not added to the club.');
            return;
        }

        await onAdded(result.data);
        onClose();
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add member to &quot;{clubName}&quot;</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Autocomplete
                        options={userOptions}
                        value={selectedUser}
                        loading={usersLoading}
                        filterOptions={options => options}
                        getOptionLabel={userLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input' || reason === 'clear') setUserSearch(value);
                        }}
                        onChange={(_, value) => setSelectedUser(value)}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar src={option.photoUrl} sx={{ width: 32, height: 32 }} />
                                    <Box>
                                        <Typography variant="body2">{userName(option)}</Typography>
                                        <Typography variant="caption" color="text.secondary">ID {option.id}</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="User"
                                placeholder="Search by name, email, or user ID"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {usersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button variant="contained" onClick={addMember} disabled={submitting || !selectedUser}>
                    {submitting ? 'Adding...' : 'Add member'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
